/**
 * THEODORE — HubSpot form submit (sections/theodore-hubspot-form.liquid).
 * Posts the form's fields to HubSpot's Forms API v3 with the same form id
 * the old site used, so leads keep landing in the CRM. The hubspotutk
 * cookie (set by the tracking script) ties the submission to the visit.
 * Nothing here depends on HubSpot's embed script.
 */
(function () {
  'use strict';

  function cookie(name) {
    var m = document.cookie.match('(?:^|; )' + name + '=([^;]*)');
    return m ? decodeURIComponent(m[1]) : '';
  }

  function friendly(errors, fallback) {
    var seen = {};
    var out = [];
    (errors || []).forEach(function (e) {
      var t = e.errorType || '';
      var msg = '';
      if (t === 'INVALID_EMAIL' || t === 'BLOCKED_EMAIL') msg = 'Please check the email address.';
      else if (t === 'REQUIRED_FIELD') msg = 'Please fill in every required field.';
      else if (t === 'NUMBER_OUT_OF_RANGE' || t === 'INVALID_NUMBER') msg = 'Please check the number you entered.';
      else if (t === 'INPUT_TOO_LARGE') msg = 'That message is too long.';
      else msg = e.message || '';
      if (msg && !seen[msg]) { seen[msg] = true; out.push(msg); }
    });
    return out.join(' ') || fallback;
  }

  /* ── Validation ──────────────────────────────────────────────
     The form reports its own errors. It is novalidate, and the browser's
     bubble cannot stand in: the page scrolls smoothly (assets/base.css), so
     Chrome is still scrolling to the field when it goes to anchor the bubble
     and drops it. Each field that needs attention gets aria-invalid and a
     line of text under it (the wording is the field's data-missing or
     data-invalid, set in the section), the status line names the first one,
     and that field is focused clear of the sticky header. */
  function labelText(el) {
    var label = el.id && el.form ? el.form.querySelector('label[for="' + el.id + '"]') : null;
    if (!label) return 'this field';
    var copy = label.cloneNode(true);
    copy.querySelectorAll('[aria-hidden="true"]').forEach(function (star) { star.remove(); });
    return copy.textContent.trim() || 'this field';
  }

  function problem(el) {
    if (!el.willValidate) return '';
    var wrong = el.dataset.invalid || 'Please check ' + labelText(el) + '.';
    // Something is typed that the browser cannot read as a value (a date with
    // the year still blank). The value reads as empty, but the field is not.
    if (el.validity.badInput) return wrong;
    // A date before today, or past the furthest one the form offers (bug hunt #380).
    if (el.validity.rangeUnderflow && el.dataset.early) return el.dataset.early;
    if (el.validity.rangeOverflow && el.dataset.late) return el.dataset.late;
    // A required field holding only spaces passes the browser's check, but
    // the value is trimmed away before it is sent.
    if (el.validity.valueMissing || (el.required && !String(el.value || '').trim())) {
      return el.dataset.missing || 'Please fill in ' + labelText(el) + '.';
    }
    // A web address is judged as it will be sent (webAddress below), not by
    // the browser's url check, which refuses "www.linkedin.com/in/name" and
    // takes "https://not a url". Only spaces in an optional one is no address.
    if (el.type === 'url') return !String(el.value || '').trim() || webAddress(el.value) ? '' : wrong;
    if (!el.validity.valid) return wrong;
    return '';
  }

  // The address a url field sends, or '' when it is not a web address. One
  // typed without its scheme ("www.linkedin.com/in/name", what people copy
  // from the address bar) gets https:// (bug hunt #385); it must then be http
  // or https with a dotted host and no spaces, so a bare handle ("john-smith")
  // or a phrase is refused rather than sent as https://john-smith. What is
  // sent is the address as the browser reads it (a stray @, a backslash or a
  // third slash tidied away); the field itself is never rewritten, so a
  // refused value stays as it was typed.
  function webAddress(raw) {
    var value = String(raw || '').trim();
    if (!value || /\s/.test(value)) return '';
    // http(s) with a slash too few ("https:/www…") is the browser's to repair
    if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(value) && !/^https?:/i.test(value)) value = 'https://' + value.replace(/^\/+/, '');
    var url;
    try { url = new URL(value); } catch (e) { return ''; }
    if (!/^https?:$/.test(url.protocol) || url.username || url.password) return '';
    if (!/^([a-z0-9-]+\.)+[a-z][a-z0-9-]+$/i.test(url.hostname)) return '';
    return url.href;
  }

  function noteFor(el) {
    return el.id ? document.getElementById(el.id + '-error') : null;
  }

  function mark(el, text) {
    var note = noteFor(el);
    if (!note && el.id) {
      note = document.createElement('p');
      note.className = 'td-hsform__error';
      note.id = el.id + '-error';
      (el.closest('.td-hsform__field') || el.parentNode).appendChild(note);
    }
    if (note) {
      note.textContent = text;
      el.setAttribute('aria-describedby', note.id);
    }
    el.setAttribute('aria-invalid', 'true');
  }

  function unmark(el) {
    var note = noteFor(el);
    if (note) note.remove();
    el.removeAttribute('aria-invalid');
    el.removeAttribute('aria-describedby');
  }

  // A date picker offers today to two years out, in the visitor's own time
  // zone, so a past day or a year like 20026 cannot be sent (bug hunt #380).
  function limitDates(form) {
    var pad = function (n) { return (n < 10 ? '0' : '') + n; };
    var iso = function (d) { return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); };
    var today = new Date();
    var last = new Date(today.getFullYear() + 2, today.getMonth(), today.getDate());
    // Only when the day has moved, and never under a half-typed date (the
    // year still blank): Chrome empties one whenever min or max is set, even
    // to the same value, at start-up as at Send.
    form.querySelectorAll('input[type="date"][data-future]').forEach(function (el) {
      if (el.validity.badInput) return;
      if (el.min !== iso(today)) el.min = iso(today);
      if (el.max !== iso(last)) el.max = iso(last);
    });
  }

  function reveal(el) {
    // The field's wrapper carries the scroll margin that clears the header,
    // and brings the label with it. 'nearest' leaves a field that is already
    // in view where it is; 'instant' because a smooth scroll would still be
    // running when the field takes focus.
    var box = el.closest('.td-hsform__field') || el;
    try { box.scrollIntoView({ block: 'nearest', behavior: 'instant' }); } catch (e) { box.scrollIntoView(); }
    try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
  }

  function init(form) {
    if (form.__thdrHs) return;
    form.__thdrHs = true;
    var status = form.querySelector('.td-hsform__status');
    var button = form.querySelector('button[type="submit"]');
    var flagged = false; // the status line is showing a field's message
    limitDates(form);

    function say(text, kind) {
      if (!status) return;
      status.textContent = text;
      status.className = 'td-hsform__status' + (kind ? ' td-hsform__status--' + kind : '');
    }

    function check() {
      var first = null;
      Array.prototype.forEach.call(form.elements, function (el) {
        if (!el.name || el.name === '_gotcha') return;
        var text = problem(el);
        if (!text) { unmark(el); return; }
        mark(el, text);
        if (!first) first = el;
      });
      return first;
    }

    // A marked field follows what is typed into it. Its line is re-worded when
    // the kind of problem changes (empty, then wrongly formatted) and removed
    // as soon as the field is put right. The status line keeps naming the
    // first field still marked, and is written only when its text changes so
    // a screen reader does not hear the same line again on every keystroke.
    function settle(event) {
      var el = event.target;
      if (!el || !el.getAttribute || el.getAttribute('aria-invalid') !== 'true') return;
      var text = problem(el);
      if (text) {
        var note = noteFor(el);
        if (!note || note.textContent === text) return;
        note.textContent = text;
      } else {
        unmark(el);
      }
      if (!flagged) return;
      var first = form.querySelector('[aria-invalid="true"]');
      var line = first && noteFor(first) ? noteFor(first).textContent : '';
      flagged = Boolean(line);
      if (status && status.textContent !== line) say(line, line ? 'error' : '');
    }
    form.addEventListener('input', settle);
    form.addEventListener('change', settle);

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      say('', '');
      flagged = false;
      limitDates(form); // a page left open past midnight moves today on
      var invalid = check();
      if (invalid) {
        var note = noteFor(invalid);
        flagged = true;
        say(note ? note.textContent : problem(invalid), 'error');
        reveal(invalid);
        return;
      }
      var honeypot = form.querySelector('[name="_gotcha"]');
      if (honeypot && honeypot.value) { say(form.dataset.success, 'sent'); return; }

      var fields = [];
      var composed = [];
      form.querySelectorAll('[name]').forEach(function (el) {
        if (el.name === '_gotcha' || el.type === 'submit' || el.type === 'button') return;
        if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
        var value = el.type === 'url' ? webAddress(el.value) : (el.value || '').trim();
        if (!value) return;
        // A data-compose field is one labelled line of the contact's `message`, not a property of its own.
        if (el.dataset.compose) {
          composed.push(el.dataset.compose + ':' + (value.indexOf('\n') > -1 ? '\n' : ' ') + value);
          return;
        }
        fields.push({ objectTypeId: el.dataset.object || '0-1', name: el.name, value: value });
      });
      if (composed.length) fields.push({ objectTypeId: '0-1', name: 'message', value: composed.join('\n') });

      var payload = {
        fields: fields,
        context: { pageUri: location.href, pageName: document.title }
      };
      var hutk = cookie('hubspotutk');
      if (hutk) payload.context.hutk = hutk;

      var url = 'https://api.hsforms.com/submissions/v3/integration/submit/' + form.dataset.portal + '/' + form.dataset.form;
      if (button) button.disabled = true;
      say('Sending…', '');

      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          return res.json().catch(function () { return {}; }).then(function (data) { return { ok: res.ok, data: data }; });
        })
        .then(function (r) {
          if (r.ok) {
            form.reset();
            form.classList.add('is-sent');
            say(form.dataset.success || 'Thank you.', 'sent');
            try {
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({ event: 'hubspot_form_submitted', form_id: form.dataset.form });
            } catch (e) { /* analytics is optional */ }
            return;
          }
          say(friendly(r.data && r.data.errors, 'We could not send that. Please try again, or email ' + form.dataset.fallback + '.'), 'error');
          if (button) button.disabled = false;
        })
        .catch(function () {
          say('We could not send that. Please email ' + form.dataset.fallback + ' instead.', 'error');
          if (button) button.disabled = false;
        });
    });
  }

  /* ── Collapsible sections ("Show as a dropdown") ─────────────
     The section's anchor id sits on the wrapper around the <details>. Open
     it when the address points there (load and hashchange) and when a link
     to it is clicked, so "#request" links still land on an open form. */
  function disclosureFor(id) {
    if (!id) return null;
    var target = document.getElementById(id);
    var details = target && target.querySelector('details[data-td-hsform-disc]');
    return details ? { target: target, details: details } : null;
  }

  function hashId(hash) {
    var raw = (hash || '').replace(/^#/, '');
    try { return decodeURIComponent(raw); } catch (e) { return raw; }
  }

  function openFromHash(scroll) {
    var d = disclosureFor(hashId(location.hash));
    if (!d) return;
    d.details.open = true;
    if (scroll) d.target.scrollIntoView({ block: 'start' });
  }

  if (!window.__tdHsDisclosure) {
    window.__tdHsDisclosure = true;
    window.addEventListener('hashchange', function () { openFromHash(true); });
    // Capture phase: the disclosure is open before the browser scrolls to it.
    document.addEventListener('click', function (event) {
      var link = event.target.closest && event.target.closest('a[href*="#"]');
      if (!link) return;
      var url;
      try { url = new URL(link.href, location.href); } catch (e) { return; }
      if (url.origin !== location.origin || url.pathname !== location.pathname) return;
      var d = disclosureFor(hashId(url.hash));
      if (!d) return;
      d.details.open = true;
      // Same hash as the address already has (someone closed the form and
      // clicked again): no hashchange fires, and the browser's own same-hash
      // navigation fires a popstate carrying the saved scroll position, which
      // assets/scroll-container.js restores, so the page would not move.
      // Nothing needs navigating, so skip it and scroll here.
      if (url.hash === location.hash) {
        event.preventDefault();
        requestAnimationFrame(function () { d.target.scrollIntoView({ block: 'start' }); });
      }
    }, true);
    // Theme editor: selecting the section shows the form.
    document.addEventListener('shopify:section:select', function (event) {
      var details = event.target && event.target.querySelector && event.target.querySelector('details[data-td-hsform-disc]');
      if (details) details.open = true;
    });
  }

  function boot() {
    document.querySelectorAll('form[data-td-hsform]').forEach(init);
    openFromHash(true);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', function () {
    document.querySelectorAll('form[data-td-hsform]').forEach(init);
  });
})();
